import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NovelService, Novel } from '../../services/novelService';
import { trackAction } from '../../services/activityService';
import { useUISettings } from '../../contexts/UISettingsContext';
import PageHeader from '../common/PageHeader';
import './Novels.css';

interface NovelsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const Novels: React.FC<NovelsProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { settings } = useUISettings();
  const [novels, setNovels] = useState<Novel[]>([]);
  const [publicNovels, setPublicNovels] = useState<Novel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newNovelTitle, setNewNovelTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredNovels, setFilteredNovels] = useState<Novel[]>([]);
  const [activeTab, setActiveTab] = useState<'my' | 'public'>('my');

  useEffect(() => {
    if (user) {
      loadNovels();
      loadPublicNovels();
    }
  }, [user]);

  useEffect(() => {
    // Filter novels based on search query, active tab, and hidden state
    const sourceNovels = activeTab === 'my' ? novels : publicNovels;
    const visibleNovels = activeTab === 'my'
      ? sourceNovels.filter(novel => settings.showHiddenContent || !novel.is_hidden)
      : sourceNovels;

    if (searchQuery.trim() === '') {
      setFilteredNovels(visibleNovels);
    } else {
      const filtered = visibleNovels.filter(novel =>
        novel.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        novel.genre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        novel.author_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredNovels(filtered);
    }
  }, [novels, publicNovels, searchQuery, activeTab, settings.showHiddenContent]);

  const loadNovels = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const response = await NovelService.getUserNovels(user.id, {
        limit: 100,
        sort_order: 'DESC'
      });
      setNovels(response);
    } catch (err) {
      console.error('Error loading novels:', err);
      setError('Failed to load novels');
    } finally {
      setLoading(false);
    }
  };

  const loadPublicNovels = async () => {
    try {
      const response = await NovelService.getPublicNovels({
        limit: 100,
        sort_order: 'DESC'
      });
      setPublicNovels(response);
    } catch (err) {
      console.error('Error loading public novels:', err);
    }
  };

  const handleCreateNovel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newNovelTitle.trim()) return;

    try {
      setError(null);
      const newNovel = await NovelService.createNovel({
        user_id: user.id,
        title: newNovelTitle.trim()
      });
      setNovels([newNovel, ...novels]);
      setNewNovelTitle('');
      setShowCreateForm(false);
      trackAction(user, 'NOVEL_CREATE', {
        novelId: newNovel.novel_id,
        title: newNovel.title,
      });
    } catch (err) {
      console.error('Error creating novel:', err);
      setError('Failed to create novel');
    }
  };

  const handleDeleteNovel = async (novelId: string) => {
    if (!window.confirm('Are you sure you want to delete this novel and all its chapters?')) return;

    try {
      setError(null);
      await NovelService.deleteNovel(novelId);
      setNovels(novels.filter(novel => novel.novel_id !== novelId));
    } catch (err) {
      console.error('Error deleting novel:', err);
      setError('Failed to delete novel');
    }
  };

  const handleNovelClick = (novelId: string, isPublic: boolean = false) => {
    if (isPublic && activeTab === 'public') {
      navigate(`/novel/public/${novelId}`);
    } else {
      navigate(`/novel/${novelId}`);
    }
  };

  const handleToggleHidden = async (novelId: string, currentlyHidden: boolean) => {
    try {
      setError(null);
      await NovelService.updateNovelHidden(novelId, !currentlyHidden);
      await loadNovels();
    } catch (err) {
      console.error('Error updating novel hidden state:', err);
      setError('Failed to update novel visibility');
    }
  };

  const handleToggleVisibility = async (novelId: string, currentlyPublic: boolean) => {
    try {
      setError(null);
      await NovelService.updateNovelVisibility(novelId, !currentlyPublic);
      // Reload novels to reflect changes
      await loadNovels();
      if (!currentlyPublic) {
        await loadPublicNovels(); // Refresh public list if making public
      }
    } catch (err) {
      console.error('Error updating novel visibility:', err);
      setError('Failed to update novel visibility');
    }
  };

  const handleStatusUpdate = async (novelId: string, newStatus: 'on-going' | 'pending' | 'completed') => {
    try {
      setError(null);
      const novel = novels.find(n => n.novel_id === novelId);
      if (!novel) return;

      await NovelService.updateNovel(novelId, {
        title: novel.title,
        status: newStatus
      });
      // Reload novels to reflect changes
      await loadNovels();
    } catch (err) {
      console.error('Error updating novel status:', err);
      setError('Failed to update novel status');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  return (
    <div className="novels-page-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/novel</h1>}
        rightActions={
          <button 
            className="novel-close-btn"
            onClick={onClose}
          >
            ✕
          </button>
        }
      />

      <div className="novels-content">
        <div className="novels-header">
          <div className="novels-tabs">
            <button
              className={`novels-tab ${activeTab === 'my' ? 'active' : ''}`}
              onClick={() => setActiveTab('my')}
            >
              My Novels
            </button>
            <button
              className={`novels-tab ${activeTab === 'public' ? 'active' : ''}`}
              onClick={() => setActiveTab('public')}
            >
              Public Novels
            </button>
          </div>
          <div className="novels-controls">
            <div className="novels-search">
              <input
                type="text"
                placeholder="Search novels..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="novels-search-input"
              />
            </div>
            {activeTab === 'my' && (
              <button
                className="novels-create-button"
                onClick={() => setShowCreateForm(true)}
              >
                + New Novel
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="novels-error">
            {error}
          </div>
        )}

        {showCreateForm && (
          <div className="novels-create-form-overlay" onClick={() => setShowCreateForm(false)}>
            <div className="novels-create-form" onClick={(e) => e.stopPropagation()}>
              <h3>Create New Novel</h3>
              <form onSubmit={handleCreateNovel}>
                <input
                  type="text"
                  placeholder="Novel title..."
                  value={newNovelTitle}
                  onChange={(e) => setNewNovelTitle(e.target.value)}
                  autoFocus
                  className="novels-title-input"
                />
                <div className="novels-form-actions">
                  <button type="submit" className="novels-form-submit">
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewNovelTitle('');
                    }}
                    className="novels-form-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="novels-loading">Loading novels...</div>
        ) : (
          <div className="novels-list">
            {filteredNovels.length === 0 ? (
              <div className="novels-empty">
                {searchQuery ? 'No novels found matching your search.' : 'No novels yet. Create your first novel!'}
              </div>
            ) : (
              filteredNovels.map((novel) => (
                <div
                  key={novel.id}
                  className="novel-card"
                  onClick={() => handleNovelClick(novel.novel_id, activeTab === 'public')}
                >
                  <div className="novel-card-header">
                    <div className="novel-card-title-area">
                      <h3 className="novel-card-title">{novel.title}</h3>
                      <div className="novel-card-tags">
                        {novel.status && (
                          <span className={`novel-status-badge novel-status-${novel.status}`}>
                            {novel.status === 'on-going' ? 'On-going' : novel.status === 'completed' ? 'Completed' : 'Pending'}
                          </span>
                        )}
                        {novel.genre && (
                          <span className="novel-card-genre">{novel.genre}</span>
                        )}
                      </div>
                    </div>
                    <div className="novel-card-actions">
                      {activeTab === 'my' && (
                        <>
                          <select
                            className="novel-status-select"
                            value={novel.status || 'pending'}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleStatusUpdate(novel.novel_id, e.target.value as 'on-going' | 'pending' | 'completed');
                            }}
                            onClick={(e) => e.stopPropagation()}
                            title="Update status"
                          >
                            <option value="pending">Pending</option>
                            <option value="on-going">On-going</option>
                            <option value="completed">Completed</option>
                          </select>
                          <button
                            className={`novel-visibility-button ${novel.is_public ? 'public' : 'private'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleVisibility(novel.novel_id, novel.is_public);
                            }}
                            title={novel.is_public ? 'Make private' : 'Make public'}
                          >
                            {novel.is_public ? '🌐' : '🔒'}
                          </button>
                          <button
                            className={`novel-hidden-button ${novel.is_hidden ? 'hidden' : 'visible'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleHidden(novel.novel_id, novel.is_hidden);
                            }}
                            title={novel.is_hidden ? 'Unhide novel' : 'Hide novel'}
                          >
                            {novel.is_hidden ? '👁️' : '🙈'}
                          </button>
                          <button
                            className="novel-delete-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNovel(novel.novel_id);
                            }}
                          >
                            ×
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="novel-card-meta">
                    {activeTab === 'public' && novel.author_name && (
                      <>
                        <span className="novel-card-author">by {novel.author_name}</span>
                        <span className="novel-card-separator">•</span>
                      </>
                    )}
                    <span className="novel-card-chapters">
                      {novel.chapters.length} {novel.chapters.length === 1 ? 'chapter' : 'chapters'}
                    </span>
                    <span className="novel-card-separator">•</span>
                    <span className="novel-card-date">
                      Updated {formatDate(novel.updated_at)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Novels;

